import React, { memo, useCallback, useEffect } from 'react';
import { HTMLAttributes, useContext, useImperativeHandle, useRef, useState } from 'react';
import { Key, SelectContext } from 'react-aria-components';

import { merge } from '@/components';
import { Text } from '@/components/core';

import { InputContext } from './PillsInputContext';

export type Item = {
  value: string;
  label?: string;
};

export type PillListActions = {
  addItem: (pill: Item) => void;
  getItems: () => Item[];
  removeItem: (pill: Item) => void;
  focus: () => void;
  updateItem: (item: Item) => void;
};

export type ListProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  children: (pill: Item, handle: PillListActions) => React.ReactNode;
  autoSeparate?: string[];
  onItemsChange?: (items: Item[]) => void;
  initialValues?: Item[];
  isInputDisabled?: boolean;
  placeholder?: string;
  mapValue?: (key: Key) => Item;
};

export const IList = ({
  children,
  autoSeparate,
  onItemsChange,
  initialValues,
  isInputDisabled,
  placeholder,
  mapValue,
  className,
  ...rest
}: ListProps) => {
  const { pillsRef } = useContext(InputContext);

  const [pills, setPills] = useState<Item[]>([...(initialValues ?? [])]);
  const [cursor, setCursor] = useState<number>(pills.length);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (onItemsChange) onItemsChange(pills);
  }, [pills, onItemsChange]);

  const clampCursor = useCallback((cursor: number, max: number) => {
    return Math.max(Math.min(cursor, max), 0);
  }, []);

  useImperativeHandle(
    pillsRef,
    () => ({
      addItem: (pill: Item) => {
        const max = pills.length + 1;
        setPills((pills) => pills.toSpliced(cursor, 0, pill));
        setCursor((cursor) => clampCursor(cursor + 1, max));
      },
      updateItem: (pill: Item) => setPills((pills) => pills.map((p) => (p.value === pill.value ? pill : p))),
      getItems: () => pills,
      removeItem: (pill: Item) => {
        const index = pills.findIndex((p) => p.value === pill.value);
        if (index === -1) return;
        const max = pills.length - 1;
        setPills((pills) => pills.toSpliced(index, 1));
        if (cursor > index) setCursor((cursor) => clampCursor(cursor - 1, max));
      },
      focus: () => {
        setCursor(pills.length);
        inputRef.current?.focus();
        setIsFocused(true);
      },
    }),
    [cursor, clampCursor, pills]
  );

  const saveAsPill = useCallback(() => {
    if (!inputRef.current) return;

    const text = inputRef.current.textContent;
    if (text) {
      const parts = text.trim().split(/\s/);
      for (const value of parts) {
        pillsRef?.current?.addItem(mapValue ? mapValue(value) : { value });
      }

      inputRef.current.textContent = '';
    }

    setIsFocused(false);
  }, [mapValue, pillsRef]);

  const onInput = useCallback(() => {
    const text = inputRef?.current?.textContent;

    if (text) {
      if (autoSeparate?.includes(text) || text.match(/\s$/)) {
        saveAsPill();
      }
    }
  }, [autoSeparate, saveAsPill]);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (!inputRef.current?.textContent) {
        switch (e.key) {
          case 'Backspace': {
            const max = pills.length - 1;
            setPills((pills) => [...pills.slice(0, cursor - 1), ...pills.slice(cursor, pills.length)]);
            setCursor((cursor) => clampCursor(cursor - 1, max));
            break;
          }
          case 'Delete':
            setPills((pills) => [...pills.slice(0, cursor), ...pills.slice(cursor + 1, pills.length)]);
            break;
          case 'ArrowLeft':
            setCursor((cursor) => clampCursor(cursor - 1, pills.length));
            break;
          case 'ArrowRight':
            setCursor((cursor) => clampCursor(cursor + 1, pills.length));
            break;
        }
      }
    },
    [clampCursor, cursor, pills.length]
  );

  return (
    <div
      className={merge(
        'bg-white',
        'border-1',
        'border-gray-200',
        'flex',
        'flex-wrap',
        'font-regular',
        'has-[:disabled]:bg-gray-50',
        'items-center',
        'outline-none',
        'p-xs',
        'rounded-lg',
        'text-gray-900',
        'text-sm',
        'w-full',
        className
      )}
      onClick={pillsRef?.current?.focus}
      onBlur={saveAsPill}
      {...rest}
    >
      {pills?.length === 0 && !isFocused && (
        <Text slot="description" variant="tertiary">
          {placeholder}
        </Text>
      )}
      {pillsRef?.current && pills.slice(0, cursor).map((p) => children(p, pillsRef.current!))}
      {!isInputDisabled ? (
        <span
          ref={inputRef}
          contentEditable
          onInput={onInput}
          onKeyDown={handleKeydown}
          className="px-xs text-sm text-gray-600 outline-0"
        />
      ) : null}
      {pillsRef?.current && pills.slice(cursor, pills.length).map((p) => children(p, pillsRef.current!))}
    </div>
  );
};

export const List = memo(IList) as typeof IList;

export type PillsInputProps = {
  pillsRef?: React.RefObject<PillListActions | null>;
  mapValue?: (key: Key) => Item;
} & HTMLAttributes<HTMLDivElement>;

const IInput = ({ children, mapValue, className, ...rest }: PillsInputProps) => {
  const ref = useRef<PillListActions>(null);

  const onSelectionChange = useCallback(
    (key: Key) => {
      const val = mapValue ? mapValue(key) : { value: key.toString() };
      ref.current?.addItem(val);
    },
    [mapValue]
  );

  return (
    <InputContext.Provider value={{ pillsRef: ref }}>
      <SelectContext.Provider value={{ onSelectionChange }}>
        <div className={className} {...rest}>
          {children}
        </div>
      </SelectContext.Provider>
    </InputContext.Provider>
  );
};

export const Input = memo(IInput) as typeof IInput;
